// ==========================================
// BUYERHEADER.JSX - UPDATED VERSION
// Estate Experts - Buyer Dashboard Header
// With Notification Dropdown, Profile Dropdown & Active Navigation
// ==========================================

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaCog,
  FaBell,
  FaHeart,
  FaChevronDown,
  FaUser,
  FaHome,
  FaCalendarCheck,
  FaComments,
  FaCreditCard,
  FaSignOutAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaBellSlash,
  FaUserCheck
} from 'react-icons/fa';
import '../../../assets/css/sellers/sellerheader/sellerheader.css';
import AppContext from 'context/AppContext';
import { FiAlertTriangle, FiBell, FiCalendar, FiHelpCircle, FiMessageSquare, FiStar, FiUserCheck } from 'react-icons/fi';
import axios from 'axios';
import { MdOutlineRealEstateAgent } from 'react-icons/md';

function SellerHeader({ pageTitle = 'Dashboard', isSidebarExpanded = false }) {
  const navigate = useNavigate();

  // ==========================================
  // REFS FOR CLICK OUTSIDE DETECTION
  // ==========================================
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  // Dropdowns state
  const { loggedInSeller, baseUrlForFiles, authToken, baseUrl } = useContext(AppContext);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);

  const viewAllNotification = () => {
    axios.get(`${baseUrl}/seller/notification/viewAllNotification`, {

      headers: {
        "Authorization": `Bearer ${authToken}`
      },
    })
      .then((response) => {
        console.log(response.data);
        if (response.data.success == true) {
          const updatedNotifications = response.data.notifications.map((item) => {
            let icon = <FiBell />;
            let color = "#3b82f6";

            const category = (item.category || '').toLowerCase().trim();

            if (category === "property") {
              icon = <FiUserCheck />;
              color = "#3b82f6";
            }
            else if (category === "visit request") {
              icon = <FaUserCheck />;
              color = "#10b981";
            }
            else if (category === "kyc") {
              icon = <MdOutlineRealEstateAgent />;
              color = "#dc2626";
            }
            else if (category === "message") {
              icon = <FiMessageSquare />;
              color = "#8b5cf6";
            }
            else if (category === "helpsupport") {
              icon = <FiHelpCircle />;
              color = "#f59e0b";
            }
            return { ...item, icon, color };
          });
          setNotifications(updatedNotifications);
        }

      }).catch((error) => {
        console.log(error);
      })
  }
  useEffect(() => {
    viewAllNotification();
  }, []);


  // ==========================================
  // CLICK OUTSIDE TO CLOSE DROPDOWNS
  // ==========================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close notifications
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }

      // Close profile dropdown
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }

      // Close search results
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ==========================================
  // HANDLER FUNCTIONS
  // ==========================================

  // Toggle notification dropdown
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setIsProfileDropdownOpen(false);
  };

  // Toggle profile dropdown
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setShowNotifications(false);
  };
  const getProfileImageUrl = (image) => {
    if (!image) return "/default-avatar.png";

    if (image.startsWith("http")) return image;

    return `${baseUrlForFiles.replace(/\/$/, "")}/${image.replace(/^\//, "")}`;
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, unread: false })));
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(notifications.map(n =>
      n.id === notification.id ? { ...n, unread: false } : n
    ));

    // Navigate based on notification type
    // Add your navigation logic here
    console.log('Notification clicked:', notification);
  };

  // Show more notifications (navigate to notifications page)
  const handleShowMoreNotifications = () => {
    setShowNotifications(false);
    navigate('/seller/notification');
  };

  // Handle search
  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    // Simulate API call (Replace with actual API)
    setTimeout(() => {
      // Mock search results
      const mockResults = [
        {
          id: 1,
          title: '5 Marla House in DHA Phase 5',
          location: 'DHA, Lahore',
          price: 'PKR 2.5 Crore',
          image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=200&h=200&fit=crop'
        },
        {
          id: 2,
          title: '10 Marla House in Bahria Town',
          location: 'Bahria Town, Lahore',
          price: 'PKR 1.8 Crore',
          image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=200&h=200&fit=crop'
        },
        {
          id: 3,
          title: '1 Kanal House in Model Town',
          location: 'Model Town, Lahore',
          price: 'PKR 3.2 Crore',
          image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&h=200&fit=crop'
        }
      ].filter(property =>
        property.title.toLowerCase().includes(query.toLowerCase()) ||
        property.location.toLowerCase().includes(query.toLowerCase())
      );

      setSearchResults(mockResults);
      setIsSearching(false);
    }, 500);
  };

  // Handle search result click
  const handleSearchResultClick = (property) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(`/seller/property/${property.id}`);
  };

  // Handle dropdown menu item click
  const handleMenuItemClick = (item) => {
    setIsProfileDropdownOpen(false);
    navigate(item.path);
  };

  // Handle logout
  const handleLogout = () => {
    // Clear authentication
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userProfile');

    // Show logout message
    console.log('Logout successful');

    // Redirect to login
    navigate('/seller/login');
  };

  // ==========================================
  // PROFILE DROPDOWN MENU ITEMS
  // ==========================================

  const profileMenuItems = [
    { id: 1, label: 'My Profile', icon: <FaUser />, path: '/seller/profile' },
    { id: 2, label: 'Settings', icon: <FaCog />, path: '/seller/setting' }
  ];

  // ==========================================
  // CALCULATE UNREAD NOTIFICATIONS COUNT
  // ==========================================

  const unreadCount = notifications.filter(n => n.unread).length;

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <header className={`sellerheader-container ${isSidebarExpanded ? 'sellerheader-expanded' : 'sellerheader-collapsed'}`}>
      <div className="sellerheader-wrapper">

        {/* ==========================================
            LEFT SECTION - Page Title
            ========================================== */}
        <div className="sellerheader-left">
          <h1 className="sellerheader-page-title">{pageTitle}</h1>
        </div>

        {/* ==========================================
            CENTER SECTION - Search Bar
            ========================================== */}
        <div className="sellerheader-center" ref={searchRef}>


          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="sellerheader-search-results">
              {isSearching ? (
                <div className="sellerheader-search-no-results">
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((property) => (
                  <div
                    key={property.id}
                    className="sellerheader-search-result-item"
                    onClick={() => handleSearchResultClick(property)}
                  >
                    <img
                      src={property.image}
                      alt={property.title}
                      className="sellerheader-search-result-image"
                    />
                    <div className="sellerheader-search-result-details">
                      <div className="sellerheader-search-result-title">
                        {property.title}
                      </div>
                      <div className="sellerheader-search-result-info">
                        {property.location} • {property.price}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="sellerheader-search-no-results">
                  No properties found matching "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>

        {/* ==========================================
            RIGHT SECTION - Actions + Profile
            ========================================== */}
        <div className="sellerheader-right">

          {/* Action Buttons */}
          <div className="sellerheader-actions">

            {/* Notifications Button with Dropdown */}
            <div className="sellerheader-notification-dropdown" ref={notificationRef}>
              <button
                className="sellerheader-action-btn sellerheader-notification-btn"
                onClick={toggleNotifications}
                aria-label="Notifications"
              >
                <FaBell />
                {unreadCount > 0 && (
                  <span className="sellerheader-notification-count">{unreadCount}</span>
                )}
              </button>

              {/* Notification Panel */}
              {showNotifications && (
                <div className="sellerheader-notification-panel">
                  {/* Header */}
                  <div className="sellerheader-notification-header">
                    <h3 className="sellerheader-notification-title">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        className="sellerheader-mark-all-read"
                        onClick={markAllAsRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  {notifications.length > 0 ? (
                    <>
                      <div className="sellerheader-notification-list">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`sellerheader-notification-item ${notification.unread ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={`sellerheader-notification-icon-wrapper ${notification.type}`}>
                              {notification.icon}
                            </div>
                            <div className="sellerheader-notification-content">
                              <div className="sellerheader-notification-message">
                                {notification.title}
                              </div>
                              <div className="sellerheader-notification-time">
                                {new Date(notification.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer with Show More */}
                      <div className="sellerheader-notification-footer">
                        <button
                          className="sellerheader-show-more-btn"
                          onClick={handleShowMoreNotifications}
                        >
                          Show More
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="sellerheader-no-notifications">
                      <div className="sellerheader-no-notifications-icon">
                        <FaBellSlash />
                      </div>
                      <div className="sellerheader-no-notifications-text">
                        No notifications yet
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Profile Dropdown */}
          <div className="sellerheader-profile-dropdown" ref={profileRef}>
            <button
              className="sellerheader-profile-btn"
              onClick={toggleProfileDropdown}
            >
              <img
                src={getProfileImageUrl(loggedInSeller?.profileImage)}
                alt={loggedInSeller?.firstName || "Seller"}
                className="sellerheader-profile-avatar"
              />
              <span className="sellerheader-profile-name">{loggedInSeller?.firstName} {loggedInSeller?.lastName}</span>
              <FaChevronDown className={`sellerheader-profile-arrow ${isProfileDropdownOpen ? 'sellerheader-arrow-up' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="sellerheader-dropdown-menu">
                {/* Profile Info Section */}
                <div className="sellerheader-dropdown-profile-info">
                  <img
                    src={getProfileImageUrl(loggedInSeller?.profileImage)}
                    alt="Profile"
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      border: "2px solid red"
                    }}
                  />
                  <div className="sellerheader-dropdown-profile-details">
                    <div className="sellerheader-dropdown-name">{loggedInSeller?.firstName} {loggedInSeller?.lastName}</div>
                    <div className="sellerheader-dropdown-email">{loggedInSeller?.email}</div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="sellerheader-dropdown-items">
                  {profileMenuItems.map((item) => (
                    <button
                      key={item.id}
                      className="sellerheader-dropdown-item"
                      onClick={() => handleMenuItemClick(item)}
                    >
                      <span className="sellerheader-dropdown-icon">{item.icon}</span>
                      <span className="sellerheader-dropdown-label">{item.label}</span>
                    </button>
                  ))}
                </div>

                {/* Logout Button */}
                <button
                  className="sellerheader-dropdown-item sellerheader-logout-item"
                  onClick={handleLogout}
                >
                  <span className="sellerheader-dropdown-icon"><FaSignOutAlt /></span>
                  <span className="sellerheader-dropdown-label">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default SellerHeader;